/* eslint-disable react/function-component-definition */
import type { FunctionComponent, ReactNode, VoidFunctionComponent } from 'react';
import { defineSlotComponent, findAllForSlots } from '../../layouts/slots';
import type { ButtonProps, LoaderButtonProps } from '../../generic/Button';
import { LoaderButton, TooltipButton } from '../../generic/Button';
import Flex from '../../generic/Flex';
import ReadOnlySection from '../../generic/ReadOnlySection';
import TooltipIcon from '../../generic/TooltipIcon';
import { icons } from '../../generic/Icon';
import Paragraph from './SettingsPageParagraph';
import styles from './Settings.module.less';

type SettingsPageSectionComponent = FunctionComponent & {
  Left: FunctionComponent<{ heading: string }>;
  Right: FunctionComponent;
  Heading: FunctionComponent;
  Paragraph: FunctionComponent;
  Buttons: FunctionComponent<{ children: ReactNode[] }>;
  PropagateButton: VoidFunctionComponent<Pick<ButtonProps, 'onClick' | 'isDisabled'>>;
  SaveButton: VoidFunctionComponent<Pick<LoaderButtonProps, 'onClick' | 'isDisabled' | 'isLoading'>>;
  Indented: FunctionComponent<{ isDisabled?: boolean }>;
};

/**
 * SettingsPageSection is a component for the page header,
 * which always includes the page heading and will also usually
 * include a SaveButton (and perhaps other buttons), which is
 * provided as a slot child component.
 *
 * Used in both the global/org settings pages, as well as in the project settings.
 *
 * @example
 * <SettingsPageSection>
 *   <SettingsPageSection.Left heading="Privacy">
 *     <p>This is the privacy section.</p>
 *   </SettingsPageSection.Left>
 *   <SettingsPageSection.Right>
 *     <p>Use these settings to update project privacy.</p>
 *   </SettingsPageSection.Right>
 * </SettingsPageSection>
 */
const SettingsPageSection: SettingsPageSectionComponent = ({ children }) => {
  const [Left, Right] = findAllForSlots(['Left', 'Right'], children);
  return (
    <section className={styles.section}>
      {Left}
      {Right}
    </section>
  );
};

SettingsPageSection.Left = defineSlotComponent('Left', ({ children, heading }) => (
  <span className={styles.sectionLeft}>
    <SettingsPageSection.Heading>
      {heading}
    </SettingsPageSection.Heading>
    {children}
  </span>
));

SettingsPageSection.Right = defineSlotComponent('Right', ({ children }) => (
  <span className={styles.sectionRight}>
    {children}
  </span>
));

SettingsPageSection.Heading = ({ children }) => (
  <div className={styles.sectionSubheading}>
    {children}
  </div>
);

SettingsPageSection.Paragraph = ({ children }) => (
  <Paragraph size="small">
    {children}
  </Paragraph>
);

SettingsPageSection.Buttons = ({ children }) => (
  <Flex direction="column" align="flex-start" gap="12px">
    {children}
  </Flex>
);

SettingsPageSection.PropagateButton = ({ onClick, isDisabled }) => (
  <div className={styles.propagate}>
    <TooltipButton
      id="propagate-settings-button-tooltip"
      type="secondary"
      size="medium"
      onClick={onClick}
      isDisabled={isDisabled}
      tooltip="Save settings before propagating them to projects"
      showTooltip={isDisabled}
    >
      Propagate settings...
    </TooltipButton>
    <TooltipIcon
      id="propagate-settings"
      tooltip="You can choose which settings should be applied to all existing projects."
      icon={icons.infoCircle}
      showTooltip
    />
  </div>
);

SettingsPageSection.SaveButton = ({ onClick, isDisabled, isLoading }) => (
  <LoaderButton
    type="primary"
    size="medium"
    onClick={onClick}
    isDisabled={isDisabled}
    isLoading={isLoading}
  >
    Save
  </LoaderButton>
);

/**
 * Some settings are effectively "nested" or are controlled
 * by another setting, and this provides styling to indent it and make
 * it appear under the parent setting.
 *
 * An example of this is in project notification settings, where there
 * is a checkbox at the top which enabled/disables all notifications,
 * and if disabled all the rest of the settings should appear as disabled.
 */
SettingsPageSection.Indented = ({ children, isDisabled }) => (
  <ReadOnlySection className={styles.indented} isReadOnly={isDisabled}>
    {children}
  </ReadOnlySection>
);

export default SettingsPageSection;
